import axios from 'axios';
import { useAuthStore } from '../stores/useAuthStore.js';
/**
 * Trades the httpOnly refresh cookie for an access token at application boot.
 *
 * The access token is held in memory only, so a reload starts with none. Without this step
 * the router's authenticated guard would see no token and send the user to the login form
 * on every refresh, even with a perfectly good week-long session sitting in the cookie.
 *
 * Resolves `true` when a session was restored and `false` otherwise - a first-time visitor,
 * a signed-out user, or an expired refresh token all land here, and none of them is an
 * error. The caller renders the app either way; `false` simply means unauthenticated.
 *
 * Uses a bare axios call rather than the shared client on purpose: this runs before there
 * is anything for the request interceptor to attach, and a 401 here is an expected answer
 * rather than something for the response interceptor to react to.
 */
export async function restoreSession(baseURL, timeoutMs = 5000) {
    const { setIsTokenRefreshing, updateAuthToken } = useAuthStore.getState();
    setIsTokenRefreshing(true);
    try {
        // Bounded, because the caller holds the first render until this settles: an API that
        // never answers would otherwise leave the user looking at a blank page indefinitely.
        // Timing out is treated like any other failure - the app renders signed out.
        const response = await axios.post(`${baseURL}/auth/refresh`, undefined, {
            withCredentials: true,
            timeout: timeoutMs,
        });
        const token = response.data?.token;
        if (!token)
            return false;
        updateAuthToken(token, 'Bearer');
        return true;
    }
    catch {
        // No cookie, or one the server would not honour. Either way there is no session.
        return false;
    }
    finally {
        useAuthStore.getState().setIsTokenRefreshing(false);
    }
}
